import { createSelector } from '@reduxjs/toolkit';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import { COIN_SEQUENCE } from '@/processes/Router';
import { getPoolStats } from './api';

let isCoinLoadedFirstTime = null as any;

export const selectPoolStatsRaw = (state: any) => { return getPoolStats.select(null)(state) ?? {} as any; }

export const selectPoolStats = (state: any) => { return selectPoolStatsRaw(state)?.data ?? {}; }

export const selectPoolStatsByCoin = (state: any, coin = 'BTC') => { 
  const data = selectPoolStats(state);
  return _get(data, `stats.${coin}`, {}); 
}

export const selectExchangeRate = (state: any, coin = 'BTC') => { 
  const data = selectPoolStats(state);
  return _get(data, `stats.${coin}.exchangeRates.USD`, 0); 
}

export const selectThreshold = (state: any, coin = 'BTC') => { 
  const data = selectPoolStats(state);
  return _get(data, `stats.${coin}.threshold`, 0); 
}

export const selectMinimalThreshold = (state: any, coin = 'BTC') => { 
  const data = selectPoolStats(state);
  return _get(data, `stats.${coin}.minThreshold`, 0); 
}

export const selectCryptoCurrencies = (state: any) => {
  const data = selectPoolStats(state);
  return _get(data, `cryptoCurrencies`, []);
}

export const selectCryptoCurrency = (state: any, coin = 'BTC') => {
  const data = selectPoolStats(state);
  return _get(data, `cryptoCurrencies`, []).find((item: any) => item.name === coin) || {};
}

export const selectPayoutAt = (state: any, coin = 'BTC') => { 
  const data = selectCryptoCurrency(state, coin);
  return _get(data, `payoutAt`, null);
}

export const selectProfitPerPower = (state: any, coin = 'BTC') => { 
  const data = selectCryptoCurrency(state, coin);
  return _get(data, `profitPerPower`, null);
}

export const selectExplorer = (state: any, coin = 'BTC') => { 
  const data = selectCryptoCurrency(state, coin);
  const { base, tx } = _get(data, `explorer`, {});
  return `${base}${tx}`;
}

export const selectCoinsStats = (state: any) => {
  const data = selectPoolStats(state);
  return _get(data, `stats`, {});
}

type CoinOrderMap = { [key: string]: number };

interface CryptoCurrency {
  readonly name: string;
}

const COIN_ORDER_MAP: CoinOrderMap = COIN_SEQUENCE.reduce<Record<string, number>>((acc, coin, index) => {
  acc[coin] = index;
  return acc;
}, {});

export const selectCoins = createSelector(
  selectCryptoCurrencies,
  (cryptoCurrencies: CryptoCurrency[]): string[] => {
    const coins = cryptoCurrencies
      .map((currency) => currency.name)
      .filter((coin) => coin !== 'GRAM'); // Todo: delete when backend will be ready

    return _sortBy(coins, (coin) => COIN_ORDER_MAP[coin] ?? Infinity);
  },
);

export const selectIsCoinsLoaded = (state: any) => { 
  // fix for HIP-881
  const isSuccess = isCoinLoadedFirstTime || _get(selectPoolStatsRaw(state), 'isSuccess', false);
  if (typeof isCoinLoadedFirstTime !== 'boolean' && isSuccess) {
    isCoinLoadedFirstTime = isSuccess;
  }
  return isSuccess;
}
